import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { BeltRank } from "../../MembersArea";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface AhmHakProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

export default function AhmHak({
  selectedBelt,
  getBeltColor: _getBeltColor,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: AhmHakProps) {
  if (!["BLACK/D2", "BLACK/D3", "BLACK/D4", "BLACK/D5"].includes(selectedBelt)) {
    return null;
  }

  const surface = beltTabSurfaceClass;
  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
    surface,
    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  const demoBtn =
    hasDemo?.(["ahm_hak"]) ? (
      <button
        type="button"
        className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
        onClick={() => onWatchDemo?.(["ahm_hak"])}
      >
        Watch Demonstration Video
      </button>
    ) : null;

  const patternInstructions = (
    <div>
      <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Instructions</h4>
      <ol className="list-decimal list-inside space-y-2">
        <li className="text-gray-700">Start in Ready Stance (Joonbi)</li>
        <li className="text-gray-700">Turn left into crane stance, execute slow rising block</li>
        <li className="text-gray-700">Transition to back stance, double knife-hand block</li>
        <li className="text-gray-700">Step forward into front stance, execute spear-hand strike</li>
        <li className="text-gray-700">
          Pivot into crane stance, execute high block <span className="font-semibold">KIHAP</span>
        </li>
        <li className="text-gray-700">Continue pattern sequence maintaining balance and precision</li>
        <li className="text-gray-700">
          End with final crane stance and strike <span className="font-semibold">KIHAP</span>
        </li>
        <li className="text-gray-700">Return to Ready Stance (Joonbi)</li>
      </ol>
    </div>
  );

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <h2 className="mb-2 text-lg font-semibold text-taekwondo-red md:text-xl">AhmHak — Crane on the Rock</h2>
        {demoBtn && <div className="mb-3">{demoBtn}</div>}
        <Tabs defaultValue="pattern-information" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="AhmHak">
            <TabsTrigger value="pattern-information" className={triggerClass}>
              Pattern Information
            </TabsTrigger>
            <TabsTrigger value="details" className={triggerClass}>
              Details
            </TabsTrigger>
            <TabsTrigger value="key-techniques" className={triggerClass}>
              Key Techniques
            </TabsTrigger>
            <TabsTrigger value="training-focus" className={triggerClass}>
              Training Focus
            </TabsTrigger>
            <TabsTrigger value="pattern" className={triggerClass}>
              Pattern
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
              <p className="mb-4 text-gray-700">
                AhmHak, meaning &quot;Crane on the Rock&quot;, is an advanced pattern that embodies the stability and grace of a crane perched on a
                rock. This pattern emphasizes the balance between stillness and explosive movement, characteristic of crane-style techniques.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="details" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Details</h4>
              <ul className="list-disc space-y-1 pl-5 text-gray-700">
                <li>Number of Movements: 32</li>
                <li>Pattern Diagram: Cross (+)</li>
                <li>Starting Position: Ready Stance</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="key-techniques" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Single-leg stances</li>
                <li>Precise striking points</li>
                <li>Balance transitions</li>
                <li>Crane-style blocks</li>
                <li>Swift counter-attacks</li>
                <li>Controlled power release</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="training-focus" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Training Focus</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Balance mastery</li>
                <li>Stability in motion</li>
                <li>Precise targeting</li>
                <li>Energy conservation</li>
                <li>Timing control</li>
                <li>Mental focus</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="pattern" className="mt-0 focus-visible:outline-none">
            {patternInstructions}
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg bg-gray-50 p-4">
      <div className="space-y-4">
        <div>
          {demoBtn}
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
          <p className="mb-4 text-gray-700">
            AhmHak, meaning &quot;Crane on the Rock&quot;, is an advanced pattern that embodies the stability and grace of a crane perched on a rock.
            This pattern emphasizes the balance between stillness and explosive movement, characteristic of crane-style techniques.
          </p>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Details</h4>
          <ul className="list-disc space-y-1 pl-5 text-gray-700">
            <li>Number of Movements: 32</li>
            <li>Pattern Diagram: Cross (+)</li>
            <li>Starting Position: Ready Stance</li>
          </ul>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
          <ul className="list-inside list-disc space-y-1 text-gray-700">
            <li>Single-leg stances</li>
            <li>Precise striking points</li>
            <li>Balance transitions</li>
            <li>Crane-style blocks</li>
            <li>Swift counter-attacks</li>
            <li>Controlled power release</li>
          </ul>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Training Focus</h4>
          <ul className="list-inside list-disc space-y-1 text-gray-700">
            <li>Balance mastery</li>
            <li>Stability in motion</li>
            <li>Precise targeting</li>
            <li>Energy conservation</li>
            <li>Timing control</li>
            <li>Mental focus</li>
          </ul>
        </div>

        {patternInstructions}
      </div>
    </div>
  );
}
